import {useState, useMemo} from 'react';
import {DataTableHeader, DataTableSortState} from '@carbon/react';
import {sortValues} from '../../../../../lib/sort';
import {parseFilterValues} from '../../no-filters/utils/parse-filter-values';

const SORT_ORDER: DataTableSortState[] = ['NONE', 'ASC', 'DESC'];
const NUMERIC_COLUMNS = ['marginal_impact_time', 'marginal_impact_distance', 'final_mips_score'];

interface UseProviderTableSortParams {
    localProviders: any[];
    existingProviders: any[];
    filterProviderList: (providers: any[]) => any[];
    specialtyFilter: string;
    filters: any[];
    complianceScores: Array<{specialtyCode: string, name: string}>;
}

interface UseProviderTableSortReturn {
    sortColumn: string;
    sortDirection: DataTableSortState;
    tableRows: any[];
    handleSort: (header: DataTableHeader) => () => void;
}

export function useProviderTableSort({
    localProviders,
    existingProviders,
    filterProviderList,
    specialtyFilter,
    filters,
    complianceScores
}: UseProviderTableSortParams): UseProviderTableSortReturn {
    const [sortDirection, setSortDirection] = useState<DataTableSortState>(SORT_ORDER[0]);
    const [sortColumn, setSortColumn] = useState<string>('');

    // Handle sort column click - cycles through NONE → ASC → DESC
    const handleSort = (header: DataTableHeader) => {
        return () => {
            setSortColumn(header.key);
            setSortDirection((s) => sortColumn !== header.key ? SORT_ORDER[1] : SORT_ORDER[(SORT_ORDER.findIndex(o => s === o) + 1) % SORT_ORDER.length]);
        };
    };

    // Sort provider list using existing sortValues utility
    const sortProviderList = (providerList: any[]) => {
        return providerList.slice().filter((p: any) => !!p[sortColumn])
            .sort((a: any, b: any) => sortValues(a[sortColumn], b[sortColumn], sortDirection === 'ASC', NUMERIC_COLUMNS.some(c => c === sortColumn)))
            .concat(providerList.filter((p: any) => !p[sortColumn]));
    };

    // Combine local and existing providers with sorting and filtering
    const tableRows = useMemo(() => {
        // Check if any selected specialty is Acute Inpatient Hospital
        // Map specialty codes to their names and check if any match "Acute Inpatient Hospital"
        const hasAcuteInpatientHospital = specialtyFilter
            ? parseFilterValues(specialtyFilter).some(code => {
                const specialty = complianceScores.find(s => s.specialtyCode === code);
                return specialty?.name === 'Acute Inpatient Hospital';
            })
            : false;

        const sortedLocalProviders = sortProviderList(localProviders).map((p: any) => ({
            ...p,
            existing: false,
            ...(hasAcuteInpatientHospital && {beds: p.beds || 'N/A'})
        }));

        const sortedExistingProviders = sortProviderList(filterProviderList(existingProviders)).map((p: any) => ({
            ...p,
            existing: true,
            ...(hasAcuteInpatientHospital && {beds: p.beds || 'N/A'})
        }));

        return sortedLocalProviders.concat(sortedExistingProviders);
    }, [localProviders, existingProviders, sortColumn, sortDirection, specialtyFilter, filters, filterProviderList, complianceScores]);

    return {
        sortColumn,
        sortDirection,
        tableRows,
        handleSort
    };
}
